// Supplier account provisioning status values, written into `configuration.status`
// by the Logyca/GS1 supplier managers. Shared by the Monitor gate (`monitor.container.tsx`)
// and the admin supplier table (`Components/SupplierList`).
const CREATION_IN_PROGRESS = 'CREATION_IN_PROGRESS';
const ACCOUNT_READY = 'ACCOUNT_READY';
const ACCOUNT_WITHOUT_DATA = 'ACCOUNT_WITHOUT_DATA';
const ACCOUNT_PENDING_REFRESH = 'ACCOUNT_PENDING_REFRESH';

interface ProviderCredential {
  connectorType: string;
  accountEmail?: string;
  accountPassword?: string;
  credentialUpdatedAt?: string;
  createdAt?: string;
  token?: string;
  account?: string;
  id?: string;
  status?: string;
  managedFields?: string[];
  connectorConfig?: { url?: string };
}

interface PolicyConfiguration {
  id: string;
  title: string;
  type: string;
}

interface DashboardItem {
  name: string;
  id: string;
  retailer: string | null;
  /** GS1 plan-based only: the stable QuickSight dashboard id (the `id` field is a per-assignment row_id for custom dashboards). Emitted to RUM telemetry for usage aggregation. */
  quicksightId?: string;
}

interface DashboardGroup {
  group: string;
  dashboards: DashboardItem[];
}

interface DataRepositoryEntry {
  label?: string;
  pageId: string;
}

interface ClientConfiguration {
  popupMessage: string;
  isMastersEnabled: boolean;
  isTopicEnabled: boolean;
  isSecondaryDashboardEnabled: boolean;
  googleSheetURLPurchase: string;
  googleSheetURLSell: string;
  recommendationSheet: string;
  language: string;
  hasMultipleAccounts?: boolean;
  /** When false, the workspace is still being provisioned: show SupplierOnboarding and keep the sidebar locked. */
  dataReady?: boolean;
  resources: {
    title: string;
    videoId: string;
  }[];
  dataRepository?: string | DataRepositoryEntry[];
  configurationLinks: {
    title: string;
    link: string;
    description: string;
  }[];
  configurationLinksId: string[];
  externalPageLinks: {
    understock: string;
    overstock: string;
  };
  connectorTypes: string[];
  clientId: string;
  clientName?: string;
  /** Human-readable account name (clients.display_name) — e.g. the supplier name shown in the Logyca sidebar. */
  clientDisplayName?: string;
  tags?: string[];
  requestCredentials?: boolean;
  dashboards?: DashboardItem[] | DashboardGroup[];
  logo?: string;
  phones?: string[];
  hideBookmark?: boolean;
  policyConfiguration?: PolicyConfiguration[];
  seenPatches?: string[];
  readOnlyMasters?: boolean;
  glnType?: string;
  defaultConnectors?: string[];
  /** Legacy GS1 field: "smb" for SMB-plan clients, else null. Derived from planNameFull server-side; kept for backward compat. */
  planName?: string | null;
  /** GS1 real plan slug (plans.name): "smbs", "inventario_inteligente_supplier", etc. (or any future plan), null when the client has no plan. Sent to RUM telemetry for plan segmentation. */
  planNameFull?: string | null;
  /** Logyca-only: set while the client's plan change is still being validated or reviewed (plan-change validation, scope review) — the two supplier_manual_tasks states the dashboard list can't express, since the client still holds the dashboards of the plan they're leaving. A client with no dashboard at all needs no field: the embed endpoint 404s and MonitorContent shows the same waiting screen. */
  pendingManualTaskType?: string;
  /** Supplier account provisioning status (e.g. CREATION_IN_PROGRESS, ACCOUNT_READY, ACCOUNT_WITHOUT_DATA, ACCOUNT_PENDING_REFRESH). Absent for clients that aren't supplier accounts. */
  status?: string;
}

interface ExpiringCredential {
  id: string;
  connector_type: string;
  status: string;
}

export type {
  ClientConfiguration,
  DashboardGroup,
  DashboardItem,
  DataRepositoryEntry,
  ExpiringCredential,
  ProviderCredential,
};

export { ACCOUNT_PENDING_REFRESH, ACCOUNT_READY, ACCOUNT_WITHOUT_DATA, CREATION_IN_PROGRESS };
